import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle, BookOpen, Brain, CheckCircle2, ChevronDown, ChevronRight, Database,
  ExternalLink, Eye, FileText, Globe, Layers, Mail, Pencil, Plus, RefreshCw, Sparkles, Tag, Trash2, Upload,
  Building2, Check, X,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  SegmentedControl,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { apiClient } from '@/lib/api.ts'
import type { Tenant } from '@shared/types'
import {
  approveQuoteAiMemory,
  createQuoteAiMemory,
  deleteQuoteAiMemory,
  fetchQuoteAiMemory,
  rejectQuoteAiMemory,
  updateQuoteAiMemory,
  type DiffContext,
  type MemoryCategory,
  type MemorySource,
  type MemoryStatus,
  type QuoteAiMemory,
} from '@/api/quoteAiMemory.ts'
import {
  addConfluenceSubscription,
  fetchKnowledgeSources,
  fetchKnowledgeChunks,
  createKnowledgeSource,
  uploadKnowledgeFile,
  deleteKnowledgeSource,
  updateKnowledgeSource,
  updateKnowledgeSourceContent,
  analyzeKnowledgeBase,
  importKnowledgeUrl,
  listConfluenceSubscriptions,
  listRecentM365Mails,
  importM365Mail,
  syncConfluenceSubscription,
  removeConfluenceSubscription,
  KNOWLEDGE_USAGE_TAG_LABELS,
  type ConfluenceSubscription,
  type EmbeddingStatus,
  type KnowledgeChunk,
  type KnowledgeSource,
  type KnowledgeUsageTag,
  type AnalysisResult,
  type M365MailListItem,
} from '@/api/knowledgeBase.ts'
import { getGlobalAdapters } from '@/api/globalAdaptersAdapter.ts'
import { OptimizationResultModal } from './OptimizationResultModal.tsx'
import { QuoteRagSection } from '@/pages/artikel/sections/QuoteRagSection.tsx'
import { DocumentDetailModal } from '@/components/documents/DocumentDetailModal.tsx'
import type { CachedDocument } from '@/api/sapDocuments.ts'

// ─── Sub-tab types ────────────────────────────────────────────────────────────

type WissensSubTab = 'wissenseintraege' | 'wissensquellen'

const VALID_SUB_TABS: WissensSubTab[] = ['wissenseintraege', 'wissensquellen']

// ─── QuoteAiMemory panel ──────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<MemoryCategory, string> = {
  rule: 'Regel',
  preference: 'Präferenz',
  'product-knowledge': 'Produkt-Wissen',
  process: 'Prozess-Hinweis',
}

const STATUS_LABELS: Record<MemoryStatus, string> = {
  pending: 'Wartet auf Freigabe',
  active: 'Aktiv',
  rejected: 'Abgelehnt',
  archived: 'Archiviert',
}

const STATUS_VARIANT: Record<MemoryStatus, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning',
  active: 'success',
  rejected: 'danger',
  archived: 'neutral',
}

const SOURCE_LABELS: Record<MemorySource, string> = {
  admin: 'Admin',
  ai: 'KI-Vorschlag',
  user: 'Vertriebler',
  'edit-diff': 'Aus Angebotskorrektur',
  document: 'Aus Dokument',
}

type MemoryTab = 'pending' | 'active' | 'rejected' | 'all'

function DiffContextBadge({ diffs }: { diffs: DiffContext[] }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div>
      <button
        className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {diffs.length} Korrektur{diffs.length !== 1 ? 'en' : ''} anzeigen
      </button>
      {expanded && (
        <div className="mt-1 space-y-1.5">
          {diffs.map((d, i) => (
            <div key={i} className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs">
              <p className="font-medium text-amber-800 mb-0.5">{d.positionDescription} · {d.field}</p>
              <p className="text-red-600 line-through">{d.aiBefore}</p>
              <p className="text-green-700">{d.userAfter}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface EditorModalProps {
  open: boolean
  memory: QuoteAiMemory | null
  onOpenChange: (v: boolean) => void
  onSaved: () => void
}

function EditorModal({ open, memory, onOpenChange, onSaved }: EditorModalProps) {
  const { toast } = useToast()
  const isEdit = !!memory

  const [content, setContent] = useState(memory?.content ?? '')
  const [category, setCategory] = useState<MemoryCategory>(memory?.category ?? 'rule')
  const [priority, setPriority] = useState<number>(memory?.priority ?? 0)
  const [status, setStatus] = useState<MemoryStatus>(memory?.status ?? 'pending')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setContent(memory?.content ?? '')
    setCategory(memory?.category ?? 'rule')
    setPriority(memory?.priority ?? 0)
    setStatus(memory?.status ?? 'pending')
  }, [memory, open])

  async function save() {
    setSaving(true)
    try {
      if (isEdit && memory) {
        await updateQuoteAiMemory(memory.id, { content, category, priority, status })
        toast({ title: 'Eintrag gespeichert', tone: 'success' })
      } else {
        await createQuoteAiMemory({ content, category, priority })
        toast({ title: 'Eintrag angelegt', tone: 'success' })
      }
      onSaved()
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: err instanceof Error ? err.message : '',
        tone: 'danger',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={content.trim().length < 5}>
            Speichern
          </Button>
        </div>
      }
    >
      <div className="px-5 py-4 space-y-4">
        <Textarea
          label="Inhalt"
          hint="Prägnant formulieren (1–3 Sätze). Mindestens 5 Zeichen."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          placeholder='z.B. "Bei Teams-Migrationen immer eine Telefonie-Lizenz pro Calling-User vorschlagen."'
        />
        <Select
          label="Kategorie"
          value={category}
          onValueChange={(v) => setCategory(v as MemoryCategory)}
          options={[
            { value: 'rule', label: 'Regel' },
            { value: 'preference', label: 'Präferenz' },
            { value: 'product-knowledge', label: 'Produkt-Wissen' },
            { value: 'process', label: 'Prozess-Hinweis' },
          ]}
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-small font-medium text-gray-700 mb-1">Priorität</label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="text-caption text-gray-500 mt-1">Höher = wichtiger. Default 0.</p>
          </div>
          {isEdit && (
            <Select
              label="Status"
              value={status}
              onValueChange={(v) => setStatus(v as MemoryStatus)}
              options={[
                { value: 'pending', label: 'Wartet auf Freigabe' },
                { value: 'active', label: 'Aktiv' },
                { value: 'rejected', label: 'Abgelehnt' },
                { value: 'archived', label: 'Archiviert' },
              ]}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

function WissenseintraegePanel() {
  const { toast } = useToast()
  const [tab, setTab] = useState<MemoryTab>('pending')
  const [rows, setRows] = useState<QuoteAiMemory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<QuoteAiMemory | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const status = tab === 'all' ? undefined : (tab as MemoryStatus)
      const data = await fetchQuoteAiMemory(status ? { status } : {})
      setRows(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => { void refetch() }, [refetch])

  function openCreate() {
    setEditing(null)
    setEditorOpen(true)
  }

  function openEdit(row: QuoteAiMemory) {
    setEditing(row)
    setEditorOpen(true)
  }

  async function handleApprove(row: QuoteAiMemory) {
    try {
      await approveQuoteAiMemory(row.id)
      toast({ title: 'Eintrag freigegeben', tone: 'success' })
      await refetch()
    } catch (err) {
      toast({ title: 'Freigabe fehlgeschlagen', description: err instanceof Error ? err.message : '', tone: 'danger' })
    }
  }

  async function handleReject(row: QuoteAiMemory) {
    try {
      await rejectQuoteAiMemory(row.id)
      toast({ title: 'Eintrag abgelehnt', tone: 'success' })
      await refetch()
    } catch (err) {
      toast({ title: 'Ablehnung fehlgeschlagen', description: err instanceof Error ? err.message : '', tone: 'danger' })
    }
  }

  async function handleDelete(row: QuoteAiMemory) {
    if (!window.confirm(`Eintrag wirklich löschen?\n\n„${row.content.slice(0, 100)}…"`)) return
    try {
      await deleteQuoteAiMemory(row.id)
      toast({ title: 'Eintrag gelöscht', tone: 'success' })
      await refetch()
    } catch (err) {
      toast({ title: 'Löschen fehlgeschlagen', description: err instanceof Error ? err.message : '', tone: 'danger' })
    }
  }

  const pendingCount = rows.filter((r) => r.status === 'pending').length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-gray-400" />
          <p className="text-caption text-gray-600">
            Globaler Speicher. Aktive Einträge fließen in jeden KI-Dialog ein.
            Korrekturen an Angeboten vor dem Versand erzeugen automatisch Vorschläge (Quelle: „Aus Angebotskorrektur").
          </p>
        </div>
        <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          Neuer Eintrag
        </Button>
      </div>

      <SegmentedControl<MemoryTab>
        value={tab}
        onValueChange={setTab}
        options={[
          {
            value: 'pending',
            label: pendingCount > 0 ? `Wartet auf Freigabe (${pendingCount})` : 'Wartet auf Freigabe',
          },
          { value: 'active', label: 'Aktiv' },
          { value: 'rejected', label: 'Abgelehnt' },
          { value: 'all', label: 'Alle' },
        ]}
      />

      {loading ? (
        <LoadingState label="Wissens-Einträge werden geladen…" />
      ) : error ? (
        <ErrorState title="Fehler beim Laden" description={error} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Keine Einträge in dieser Kategorie"
          description={
            tab === 'pending'
              ? 'Aktuell warten keine Einträge auf Freigabe. Korrekturen vor dem Angebotsversand erzeugen hier automatisch Vorschläge.'
              : 'Lege einen ersten Eintrag an oder warte auf Vorschläge aus den Dialogen.'
          }
        />
      ) : (
        <DataTable<QuoteAiMemory>
          data={rows}
          columns={[
            {
              id: 'content',
              header: 'Inhalt',
              accessorKey: 'content',
              cell: ({ row }) => (
                <div className="space-y-1.5">
                  <p className="text-small text-gray-900 whitespace-pre-wrap leading-relaxed">
                    {row.original.content}
                  </p>
                  {row.original.diffContextJson && row.original.diffContextJson.length > 0 && (
                    <DiffContextBadge diffs={row.original.diffContextJson} />
                  )}
                </div>
              ),
            },
            {
              id: 'category',
              header: 'Kategorie',
              accessorKey: 'category',
              cell: ({ row }) => (
                <Badge variant="neutral" size="sm">
                  {CATEGORY_LABELS[row.original.category] ?? row.original.category}
                </Badge>
              ),
            },
            {
              id: 'status',
              header: 'Status',
              accessorKey: 'status',
              cell: ({ row }) => (
                <Badge variant={STATUS_VARIANT[row.original.status]} size="sm">
                  {STATUS_LABELS[row.original.status] ?? row.original.status}
                </Badge>
              ),
            },
            {
              id: 'source',
              header: 'Quelle',
              accessorKey: 'source',
              cell: ({ row }) => (
                <span className="text-caption text-gray-600">
                  {SOURCE_LABELS[row.original.source] ?? row.original.source}
                </span>
              ),
            },
            {
              id: 'priority',
              header: 'Priorität',
              accessorKey: 'priority',
              cell: ({ row }) => (
                <span className="text-caption text-gray-600 tabular-nums">{row.original.priority}</span>
              ),
            },
            {
              id: 'actions',
              header: '',
              cell: ({ row }) => {
                const memory = row.original
                return (
                  <div className="flex items-center gap-1 justify-end">
                    {memory.status === 'pending' && (
                      <>
                        <IconButton
                          icon={<Check />}
                          aria-label="Freigeben"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleApprove(memory)}
                          title="Freigeben"
                        />
                        <IconButton
                          icon={<X />}
                          aria-label="Ablehnen"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReject(memory)}
                          title="Ablehnen"
                        />
                      </>
                    )}
                    <IconButton
                      icon={<Pencil />}
                      aria-label="Bearbeiten"
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(memory)}
                    />
                    <IconButton
                      icon={<Trash2 />}
                      aria-label="Löschen"
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(memory)}
                    />
                  </div>
                )
              },
            },
          ]}
          getRowId={(row) => row.id}
        />
      )}

      <EditorModal
        open={editorOpen}
        memory={editing}
        onOpenChange={setEditorOpen}
        onSaved={() => {
          setEditorOpen(false)
          void refetch()
        }}
      />

    </div>
  )
}

// ─── Source content viewer modal ──────────────────────────────────────────────

/**
 * Parse "quote:sap:{tenantId}:{docEntry}" or "sap:{tenantId}:{docEntry}" refs.
 * Returns null when the ref is not a SAP quotation ref.
 */
function parseSapRef(externalRef: string | null): { tenantId: string; docEntry: number } | null {
  if (!externalRef) return null
  const sapPart = externalRef.startsWith('quote:') ? externalRef.slice('quote:'.length) : externalRef
  if (!sapPart.startsWith('sap:')) return null
  const rest = sapPart.slice('sap:'.length)
  const sep = rest.lastIndexOf(':')
  if (sep <= 0) return null
  const tenantId = rest.slice(0, sep)
  const docEntry = parseInt(rest.slice(sep + 1), 10)
  if (!tenantId || isNaN(docEntry)) return null
  return { tenantId, docEntry }
}

function SourceContentModal({
  source,
  onClose,
}: {
  source: KnowledgeSource | null
  onClose: () => void
}) {
  const [chunks, setChunks] = useState<KnowledgeChunk[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sapRef = source?.sourceType === 'sap_offer' ? parseSapRef(source.externalRef ?? null) : null

  useEffect(() => {
    // For SAP offers with a valid ref, we render DocumentDetailModal directly — no chunks needed
    if (!source || sapRef) return
    setLoading(true)
    setError(null)
    fetchKnowledgeChunks(source.id)
      .then(setChunks)
      .catch((err: unknown) => {
        const e = err as { message?: string }
        setError(e?.message ?? 'Fehler beim Laden')
      })
      .finally(() => setLoading(false))
  }, [source, sapRef])

  // For SAP offer with parsed ref → use DocumentDetailModal (matches /dokumente view)
  if (source?.sourceType === 'sap_offer' && sapRef) {
    const docStub: CachedDocument = {
      id: `sap:${sapRef.tenantId}:${sapRef.docEntry}`,
      tenantId: sapRef.tenantId,
      docType: 'quotation',
      sapDocEntry: sapRef.docEntry,
      sapDocNum: null,
      cardCode: null,
      cardName: source.title,
      docDate: null,
      docDueDate: null,
      docTotal: null,
      docCurrency: null,
      docStatus: null,
      isClosed: false,
      isCancelled: false,
      comments: null,
      linesJson: null,
      fullDataJson: null,
      lastSyncedAt: null,
      createdAt: source.createdAt,
      updatedAt: source.createdAt,
      source: 'sap',
    }
    return (
      <DocumentDetailModal
        doc={docStub}
        open={!!source}
        onOpenChange={(o) => { if (!o) onClose() }}
        onConvert={() => {}}
      />
    )
  }

  return (
    <Modal
      open={!!source}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={source?.title ?? 'Inhalt'}
      size="xl"
    >
      <div className="px-5 pb-5 space-y-3">
        {source && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="font-medium">{SOURCE_TYPE_LABELS[source.sourceType] ?? source.sourceType}</span>
            <span>·</span>
            <span>{source.chunkCount} Chunk{source.chunkCount !== 1 ? 's' : ''}</span>
          </div>
        )}

        {loading ? (
          <LoadingState label="Inhalte werden geladen…" />
        ) : error ? (
          <ErrorState title="Fehler" description={error} />
        ) : chunks.length === 0 ? (
          <EmptyState title="Keine Chunks" description="Diese Quelle enthält noch keine Inhalte." />
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {chunks.map((chunk) => (
              <div
                key={chunk.id}
                className="rounded-md border border-gray-200 bg-gray-50 p-3"
              >
                <p className="text-xs font-medium text-gray-400 mb-1.5">
                  Chunk {chunk.chunkIndex + 1}
                </p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {chunk.content}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Wissensquellen panel (existing knowledge-source management) ───────────────

const TAG_COLORS: Record<KnowledgeUsageTag, string> = {
  artikel: 'bg-violet-100 text-violet-700',
  angebote: 'bg-blue-100 text-blue-700',
  allgemein: 'bg-gray-100 text-gray-600',
}

function UsageTagBadge({ tag }: { tag: KnowledgeUsageTag }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${TAG_COLORS[tag]}`}>
      {KNOWLEDGE_USAGE_TAG_LABELS[tag]}
    </span>
  )
}

const ALL_USAGE_TAGS: KnowledgeUsageTag[] = ['artikel', 'angebote', 'allgemein']

function UsageTagSelector({
  value,
  onChange,
}: {
  value: KnowledgeUsageTag[]
  onChange: (tags: KnowledgeUsageTag[]) => void
}) {
  const toggle = (tag: KnowledgeUsageTag) => {
    onChange(value.includes(tag) ? value.filter((t) => t !== tag) : [...value, tag])
  }
  return (
    <div className="flex flex-wrap gap-2">
      {ALL_USAGE_TAGS.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => toggle(tag)}
          className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
            value.includes(tag)
              ? `${TAG_COLORS[tag]} border-current`
              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
          }`}
        >
          {KNOWLEDGE_USAGE_TAG_LABELS[tag]}
        </button>
      ))}
    </div>
  )
}

function ScopeBadge({ scopeTenantId, tenants }: { scopeTenantId: string | null; tenants: Tenant[] }) {
  if (!scopeTenantId) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
        Alle Firmen
      </span>
    )
  }
  const tenant = tenants.find((t) => t.id === scopeTenantId)
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
      <Building2 className="w-3 h-3" />
      {tenant?.name ?? scopeTenantId.slice(0, 8)}
    </span>
  )
}

const PRIORITY_LABELS: Record<number, string> = { 1: 'Hoch', 2: 'Wichtig', 3: 'Normal', 4: 'Niedrig', 5: 'Archiv' }
const PRIORITY_VARIANTS: Record<number, 'success' | 'info' | 'neutral' | 'warning' | 'danger'> = {
  1: 'success', 2: 'info', 3: 'neutral', 4: 'warning', 5: 'danger',
}

function KnowledgePriorityBadge({ priority }: { priority: number | null }) {
  if (priority === null) return <span className="text-xs text-gray-400">–</span>
  return (
    <Badge variant={PRIORITY_VARIANTS[priority] ?? 'neutral'} size="sm">
      {PRIORITY_LABELS[priority] ?? String(priority)}
    </Badge>
  )
}

function EmbeddingStatusBadge({ status, chunkCount, errorMessage }: { status?: EmbeddingStatus; chunkCount: number; errorMessage?: string | null }) {
  if (chunkCount === 0) {
    return <span className="text-xs text-gray-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />Kein Text</span>
  }
  if (!status || status === 'none') {
    const tooltip = errorMessage
      ? `Embedding fehlgeschlagen: ${errorMessage} — bitte unter Einstellungen → Adapter & Postfächer → OpenAI konfigurieren`
      : 'Keine Embeddings — Quelle nicht via KI-Suche nutzbar'
    return (
      <span className="text-xs text-red-600 flex items-center gap-1" title={tooltip}>
        <AlertTriangle className="w-3.5 h-3.5" />Fehlt
      </span>
    )
  }
  if (status === 'partial') {
    return (
      <span className="text-xs text-yellow-600 flex items-center gap-1" title="Teilweise eingebettet">
        <AlertTriangle className="w-3.5 h-3.5" />Teilweise
      </span>
    )
  }
  return (
    <span className="text-xs text-green-600 flex items-center gap-1" title="Alle Chunks eingebettet — KI-Suche aktiv">
      <CheckCircle2 className="w-3.5 h-3.5" />OK
    </span>
  )
}

function AddSourceModal({
  open,
  onClose,
  onAdded,
  tenants,
}: {
  open: boolean
  onClose: () => void
  onAdded: () => void
  tenants: Tenant[]
}) {
  const { toast } = useToast()
  const [mode, setMode] = useState<'text' | 'file' | 'confluence' | 'url' | 'm365'>('text')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [confluenceInput, setConfluenceInput] = useState('')
  const [urlInput, setUrlInput] = useState('')
  const [usageTags, setUsageTags] = useState<KnowledgeUsageTag[]>(['allgemein'])
  const [scopeTenantId, setScopeTenantId] = useState<string>('__global__')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const reset = () => {
    setMode('text'); setTitle(''); setContent(''); setFile(null)
    setConfluenceInput(''); setUrlInput(''); setUsageTags(['allgemein']); setScopeTenantId('__global__'); setError('')
  }
  const handleClose = () => { reset(); onClose() }
  const resolvedScopeId = scopeTenantId === '__global__' ? null : scopeTenantId

  const confluenceKind: 'page' | 'space' | 'unknown' = (() => {
    const t = confluenceInput.trim()
    if (!t) return 'unknown'
    if (/^\d+$/.test(t)) return 'page'
    if (/\/pages\/\d+/.test(t)) return 'page'
    if (/\/spaces\/[A-Za-z0-9~][A-Za-z0-9._~-]*(?:\/?$|\/[^p])/.test(t)) return 'space'
    if (/\/spaces\/[A-Za-z0-9~][A-Za-z0-9._~-]*\/?$/.test(t)) return 'space'
    return 'unknown'
  })()

  const handleSave = async () => {
    setError('')
    if (mode === 'text' && !content.trim()) { setError('Inhalt ist Pflicht'); return }
    if (mode === 'file' && !file) { setError('Datei auswählen'); return }
    if (mode === 'confluence' && confluenceInput.trim().length < 4) { setError('Bitte Seiten-ID oder URL eingeben'); return }
    if (mode === 'url' && !/^https?:\/\/.+/.test(urlInput.trim())) { setError('Bitte eine gültige URL eingeben (https://…)'); return }
    setSaving(true)
    try {
      if (mode === 'text') {
        const { embeddingWarning } = await createKnowledgeSource({
          title: title.trim() || content.slice(0, 60),
          content: content.trim(),
          usageTags,
          scopeTenantId: resolvedScopeId,
        })
        if (embeddingWarning) {
          toast({ title: 'Quelle gespeichert — Embedding fehlgeschlagen', description: embeddingWarning, tone: 'warning' })
        }
      } else if (mode === 'file') {
        const { embeddingWarning } = await uploadKnowledgeFile(file!, title.trim() || undefined, usageTags, resolvedScopeId)
        if (embeddingWarning) {
          toast({ title: 'Datei gespeichert — Embedding fehlgeschlagen', description: embeddingWarning, tone: 'warning' })
        }
      } else if (mode === 'confluence') {
        const sub = await addConfluenceSubscription(confluenceInput.trim())
        toast({
          title: sub.subscriptionType === 'space' ? 'Confluence-Space abonniert' : 'Confluence-Seite abonniert',
          description:
            sub.subscriptionType === 'space'
              ? `Space "${sub.title}" mit ${sub.pageCount} Seiten — wird stündlich synchronisiert.`
              : 'Seite ist eingebettet und wird stündlich auf neue Versionen geprüft.',
          tone: 'success',
        })
      } else if (mode === 'url') {
        await importKnowledgeUrl(urlInput.trim(), title.trim() || undefined)
        toast({ title: 'URL importiert', description: 'Inhalt extrahiert und eingebettet.', tone: 'success' })
      }
      onAdded()
      handleClose()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setError(e?.response?.data?.message || e?.message || 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) handleClose() }} title="Neue Wissensquelle hinzufügen" size="lg">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={mode === 'text' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<FileText className="w-4 h-4" />}
            onClick={() => setMode('text')}
          >
            Text eingeben
          </Button>
          <Button
            variant={mode === 'file' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<Upload className="w-4 h-4" />}
            onClick={() => setMode('file')}
          >
            Datei (PDF / Word / Excel / PowerPoint)
          </Button>
          <Button
            variant={mode === 'confluence' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<BookOpen className="w-4 h-4" />}
            onClick={() => setMode('confluence')}
          >
            Confluence
          </Button>
          <Button
            variant={mode === 'url' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<Globe className="w-4 h-4" />}
            onClick={() => setMode('url')}
          >
            Webseite (URL)
          </Button>
          <Button
            variant={mode === 'm365' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<Mail className="w-4 h-4" />}
            onClick={() => setMode('m365')}
          >
            Outlook-Mail
          </Button>
        </div>

        {mode !== 'confluence' && mode !== 'm365' && (
          <Input label="Titel (optional)" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z.B. Muster-Texte Notebooks" />
        )}

        {mode === 'url' && (
          <>
            <Input
              label="URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://beispiel.de/seite"
              hint="Wir holen die Seite einmalig und extrahieren den Text-Inhalt."
            />
            <p className="text-caption text-gray-500">
              Funktioniert nur für Seiten ohne aufwändiges JavaScript. Bei Single-Page-Apps bitte den
              Inhalt aus dem Browser kopieren und unter „Text eingeben" einfügen.
            </p>
          </>
        )}

        {mode === 'text' && (
          <Textarea label="Inhalt" value={content} onChange={(e) => setContent(e.target.value)} rows={8}
            placeholder="Füge hier deine Muster-Texte, Stil-Richtlinien oder Beispiele ein…" />
        )}

        {mode === 'file' && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Datei</p>
            <input type="file" accept=".pdf,.docx,.xlsx,.pptx,.txt"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" />
            <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, PowerPoint bis 20 MB</p>
          </div>
        )}

        {mode === 'confluence' && (
          <>
            <Input
              label="Seiten-ID, Page-URL oder Space-URL"
              value={confluenceInput}
              onChange={(e) => setConfluenceInput(e.target.value)}
              placeholder="z.B. 123456789, …/pages/123456789/… oder …/spaces/IT"
              hint="Page-URL ⇒ einzelne Seite. Space-URL (z.B. .../spaces/IT) ⇒ alle Seiten des Spaces."
            />
            {confluenceInput.trim() && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-caption text-gray-700 flex items-center gap-2">
                {confluenceKind === 'space' ? (
                  <>
                    <Layers className="w-4 h-4 text-brand-600" />
                    Erkannt als <strong>Space-Subscription</strong> — alle Seiten werden importiert und stündlich synchronisiert.
                  </>
                ) : confluenceKind === 'page' ? (
                  <>
                    <BookOpen className="w-4 h-4 text-brand-600" />
                    Erkannt als <strong>Einzelseite</strong> — wird stündlich auf neue Versionen geprüft.
                  </>
                ) : (
                  <>
                    <BookOpen className="w-4 h-4 text-gray-400" />
                    Eingabe sieht weder nach Page-ID noch nach Space-/Page-URL aus — bitte vollständige Confluence-URL einfügen.
                  </>
                )}
              </div>
            )}
            <p className="text-caption text-gray-500">
              Voraussetzung: unter Einstellungen → Integrationen → Confluence sind Base-URL, E-Mail und API-Token hinterlegt (pro Mandant).
            </p>
          </>
        )}

        {mode === 'm365' && <M365MailPicker onImported={() => { onAdded(); handleClose() }} />}

        {mode !== 'confluence' && mode !== 'm365' && (
          <>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Verwendung</p>
              <UsageTagSelector value={usageTags} onChange={setUsageTags} />
            </div>

            <Select
              label="Gültig für"
              value={scopeTenantId}
              onValueChange={setScopeTenantId}
              hint="Gilt diese Quelle für alle Firmen oder nur für eine bestimmte?"
              options={[
                { value: '__global__', label: 'Alle Firmen (global)' },
                ...tenants.map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          </>
        )}



        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={handleClose}>Abbrechen</Button>
        {mode !== 'm365' && (
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving
              ? 'Wird gespeichert…'
              : mode === 'confluence'
              ? 'Abonnieren & einbetten'
              : mode === 'url'
              ? 'URL importieren'
              : 'Speichern'}
          </Button>
        )}
      </div>
    </Modal>
  )
}

// ─── M365 Mail Picker ─────────────────────────────────────────────────────────

function extractErrorMessage(err: unknown): string {
  const axErr = err as { response?: { data?: unknown }; message?: string }
  const data = axErr?.response?.data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.error === 'string') return obj.error
    if (typeof obj.message === 'string') return obj.message
  }
  return typeof axErr?.message === 'string' ? axErr.message : 'Unbekannter Fehler'
}

function M365MailPicker({ onImported }: { onImported: () => void }) {
  const { toast } = useToast()
  const [mails, setMails] = useState<M365MailListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [importingId, setImportingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listRecentM365Mails(25)
      .then(rows => { if (!cancelled) setMails(rows) })
      .catch(err => { if (!cancelled) setError(extractErrorMessage(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  async function handleImport(id: string) {
    setImportingId(id)
    try {
      await importM365Mail(id)
      toast({ title: 'Mail eingebettet', tone: 'success' })
      onImported()
    } catch (err) {
      toast({ title: 'Import fehlgeschlagen', description: extractErrorMessage(err), tone: 'danger' })
    } finally {
      setImportingId(null)
    }
  }

  if (loading) return <LoadingState label="Letzte Mails werden geladen…" />
  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-caption text-red-600">{error}</p>
        <p className="text-caption text-gray-500">
          Bitte sicherstellen, dass M365 unter Einstellungen → Microsoft 365 verbunden ist.
        </p>
      </div>
    )
  }
  if (mails.length === 0) return <p className="text-caption text-gray-500">Keine Mails im Posteingang gefunden.</p>

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {mails.map(m => (
        <div
          key={m.id}
          className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-3 py-2 hover:border-brand-400 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-body font-medium text-gray-900 truncate">{m.subject || '(Kein Betreff)'}</span>
              {!m.isRead && (
                <span className="text-xs text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded">neu</span>
              )}
            </div>
            <div className="text-caption text-gray-500 truncate">
              {m.from?.emailAddress?.name || m.from?.emailAddress?.address} · {new Date(m.receivedDateTime).toLocaleString('de-DE')}
            </div>
            <div className="text-caption text-gray-400 truncate">{m.bodyPreview}</div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            loading={importingId === m.id}
            disabled={importingId !== null}
            onClick={() => handleImport(m.id)}
          >
            Importieren
          </Button>
        </div>
      ))}
    </div>
  )
}

function EditSourceModal({
  source,
  onClose,
  onSaved,
  tenants,
}: {
  source: KnowledgeSource | null
  onClose: () => void
  onSaved: () => void
  tenants: Tenant[]
}) {
  const [tags, setTags] = useState<KnowledgeUsageTag[]>([])
  const [scopeTenantId, setScopeTenantId] = useState<string>('__global__')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setTags((source?.usageTags ?? []) as KnowledgeUsageTag[])
    setScopeTenantId(source?.scopeTenantId ?? '__global__')
  }, [source])

  const handleSave = async () => {
    if (!source) return
    setSaving(true)
    try {
      await updateKnowledgeSource(source.id, {
        usageTags: tags,
        scopeTenantId: scopeTenantId === '__global__' ? null : scopeTenantId,
      })
      onSaved()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={!!source} onOpenChange={(o) => { if (!o) onClose() }} title="Quelle bearbeiten" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 font-medium truncate">{source?.title}</p>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Verwendung</p>
          <UsageTagSelector value={tags} onChange={setTags} />
        </div>
        <Select
          label="Gültig für"
          value={scopeTenantId}
          onValueChange={setScopeTenantId}
          options={[
            { value: '__global__', label: 'Alle Firmen (global)' },
            ...tenants.map((t) => ({ value: t.id, label: t.name })),
          ]}
        />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>Speichern</Button>
      </div>
    </Modal>
  )
}

function EditContentModal({
  source,
  onClose,
  onSaved,
}: {
  source: KnowledgeSource | null
  onClose: () => void
  onSaved: () => void
}) {
  const [content, setContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [warning, setWarning] = useState<string | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    setContent('')
    setWarning(null)
  }, [source])

  const handleSave = async () => {
    if (!source || !content.trim()) return
    setSaving(true)
    setWarning(null)
    try {
      const result = await updateKnowledgeSourceContent(source.id, content.trim())
      if (result.embeddingWarning) {
        setWarning(result.embeddingWarning)
      }
      toast({ title: 'Inhalt aktualisiert', description: source.title })
      onSaved()
      if (!result.embeddingWarning) onClose()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast({ title: 'Speichern fehlgeschlagen', description: e?.response?.data?.message || e?.message || 'Unbekannter Fehler', tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={!!source} onOpenChange={(o) => { if (!o) onClose() }} title="Inhalt bearbeiten" size="xl">
      <div className="space-y-4 px-1">
        <p className="text-sm text-gray-600 font-medium truncate">{source?.title}</p>
        {warning && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{warning}</span>
          </div>
        )}
        <Textarea
          label="Text"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={16}
          placeholder="Inhalt eingeben…"
          hint="Bestehende Chunks werden ersetzt und neu eingebettet."
        />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !content.trim()}>
          {saving ? 'Speichern…' : 'Speichern & neu einbetten'}
        </Button>
      </div>
    </Modal>
  )
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF', text: 'Text', email: 'E-Mail', sap_offer: 'SAP-Angebot',
  docx: 'Word', xlsx: 'Excel', pptx: 'PowerPoint', confluence: 'Confluence', url: 'URL',
}

interface UnifiedRow {
  _kind: 'source' | 'subscription'
  id: string
  title: string
  displayType: string
  usageTags: KnowledgeUsageTag[]
  scopeTenantId: string | null
  chunkCount: number
  embeddingStatus: EmbeddingStatus | undefined
  embeddingErrorMessage?: string | null
  priority: number | null
  _source: KnowledgeSource | null
  _subscription: ConfluenceSubscription | null
}

function WissensquellenPanel() {
  const [sources, setSources] = useState<KnowledgeSource[]>([])
  const [subs, setSubs] = useState<ConfluenceSubscription[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [editSource, setEditSource] = useState<KnowledgeSource | null>(null)
  const [editContentSource, setEditContentSource] = useState<KnowledgeSource | null>(null)
  const [viewSource, setViewSource] = useState<KnowledgeSource | null>(null)
  const [deleteSource, setDeleteSource] = useState<KnowledgeSource | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteSubTarget, setDeleteSubTarget] = useState<ConfluenceSubscription | null>(null)
  const [deletingSub, setDeletingSub] = useState(false)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [filterTag, setFilterTag] = useState<KnowledgeUsageTag | 'all'>('all')
  const [analyzing, setAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [filterScope, setFilterScope] = useState<'all' | 'global' | string>('all')
  const [openaiConfigured, setOpenaiConfigured] = useState<boolean | null>(null)
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  // Re-check whenever the URL changes (e.g. switching back from the Adapters tab)
  // so the banner clears immediately after saving the OpenAI adapter.
  useEffect(() => {
    getGlobalAdapters()
      .then((res) => {
        const adapters = res.data as Array<{ adapter: string; isActive: boolean }>
        setOpenaiConfigured(adapters.some((a) => a.adapter === 'openai' && a.isActive !== false))
      })
      .catch(() => setOpenaiConfigured(false))
  }, [searchParams])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [sourcesData, subsData, tenantsRes] = await Promise.all([
        fetchKnowledgeSources(),
        listConfluenceSubscriptions().catch(() => [] as ConfluenceSubscription[]),
        apiClient.get<Tenant[]>('/tenants').catch(() => ({ data: [] as Tenant[] })),
      ])
      setSources(sourcesData)
      setSubs(subsData)
      setTenants(tenantsRes.data)
    } catch {
      setError('Wissensquellen konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const handleDelete = async () => {
    if (!deleteSource) return
    setDeleting(true)
    try {
      await deleteKnowledgeSource(deleteSource.id)
      setDeleteSource(null)
      void load()
    } finally {
      setDeleting(false)
    }
  }

  const handleAnalyze = async () => {
    setAnalyzing(true)
    setAnalysisError(null)
    try {
      const result = await analyzeKnowledgeBase()
      setAnalysisResult(result)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setAnalysisError(e?.response?.data?.message || e?.message || 'Analyse fehlgeschlagen')
    } finally {
      setAnalyzing(false)
    }
  }

  const handleSyncConfluence = async (sub: ConfluenceSubscription) => {
    setSyncingId(sub.id)
    try {
      await syncConfluenceSubscription(sub.id)
      toast({ title: 'Synchronisation gestartet', description: sub.title })
      void load()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast({ title: 'Sync fehlgeschlagen', description: e?.response?.data?.message || e?.message || 'Unbekannter Fehler', tone: 'danger' })
    } finally {
      setSyncingId(null)
    }
  }

  const handleDeleteSub = async () => {
    if (!deleteSubTarget) return
    setDeletingSub(true)
    try {
      await removeConfluenceSubscription(deleteSubTarget.id)
      setDeleteSubTarget(null)
      void load()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast({ title: 'Löschen fehlgeschlagen', description: e?.response?.data?.message || e?.message || 'Unbekannter Fehler', tone: 'danger' })
    } finally {
      setDeletingSub(false)
    }
  }

  const confluenceRows: UnifiedRow[] = subs
    .filter(() => {
      if (filterScope !== 'all' && filterScope !== 'global') return false
      if (filterTag !== 'all' && filterTag !== 'allgemein') return false
      return true
    })
    .map((sub) => ({
      _kind: 'subscription' as const,
      id: sub.id,
      title: sub.title,
      displayType: sub.subscriptionType === 'space' ? 'Confluence Space' : 'Confluence Seite',
      usageTags: ['allgemein'] as KnowledgeUsageTag[],
      scopeTenantId: null,
      chunkCount: sub.totalChunkCount ?? 0,
      embeddingStatus: sub.embeddingStatus,
      embeddingErrorMessage: sub.embeddingErrorMessage,
      priority: null,
      _source: null,
      _subscription: sub,
    }))

  const sourceRows: UnifiedRow[] = sources
    .filter((s) => {
      if (s.sourceType === 'confluence') return false
      if (filterTag !== 'all' && !(s.usageTags ?? []).includes(filterTag)) return false
      if (filterScope === 'global' && s.scopeTenantId !== null) return false
      if (filterScope !== 'all' && filterScope !== 'global' && s.scopeTenantId !== filterScope) return false
      return true
    })
    .map((s) => ({
      _kind: 'source' as const,
      id: s.id,
      title: s.title,
      displayType: SOURCE_TYPE_LABELS[s.sourceType] ?? s.sourceType,
      usageTags: (s.usageTags ?? []) as KnowledgeUsageTag[],
      scopeTenantId: s.scopeTenantId,
      chunkCount: s.chunkCount,
      embeddingStatus: s.embeddingStatus,
      embeddingErrorMessage: s.embeddingErrorMessage,
      priority: s.priority ?? null,
      _source: s,
      _subscription: null,
    }))

  const unifiedData: UnifiedRow[] = [...confluenceRows, ...sourceRows]

  const columns: ColumnDef<UnifiedRow>[] = [
    {
      accessorKey: 'title',
      header: 'Titel',
      cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.title}</span>,
    },
    {
      accessorKey: 'displayType',
      header: 'Typ',
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm text-gray-500">{row.original.displayType}</span>
      ),
    },
    {
      accessorKey: 'usageTags',
      header: 'Verwendung',
      cell: ({ row }) => {
        const tags = row.original.usageTags
        return (
          <div className="flex flex-wrap gap-1">
            {tags.length === 0
              ? <span className="text-xs text-gray-400">–</span>
              : tags.map((tag) => <UsageTagBadge key={tag} tag={tag} />)}
          </div>
        )
      },
    },
    {
      accessorKey: 'scopeTenantId',
      header: 'Gültig für',
      size: 140,
      cell: ({ row }) => <ScopeBadge scopeTenantId={row.original.scopeTenantId} tenants={tenants} />,
    },
    {
      accessorKey: 'chunkCount',
      header: 'Chunks',
      size: 75,
      cell: ({ row }) => <span className="text-sm text-gray-500">{row.original.chunkCount}</span>,
    },
    {
      id: 'embeddingStatus',
      header: 'Embeddings',
      size: 110,
      cell: ({ row }) => <EmbeddingStatusBadge status={row.original.embeddingStatus} chunkCount={row.original.chunkCount} errorMessage={row.original.embeddingErrorMessage} />,
    },
    {
      accessorKey: 'priority',
      header: 'Priorität',
      size: 90,
      cell: ({ row }) => {
        if (row.original._kind === 'subscription') return <span className="text-xs text-gray-400">–</span>
        return <KnowledgePriorityBadge priority={row.original.priority} />
      },
    },
    {
      id: 'actions',
      size: 120,
      cell: ({ row }) => {
        if (row.original._kind === 'subscription') {
          const sub = row.original._subscription!
          return (
            <div className="flex items-center gap-1">
              <IconButton
                icon={<RefreshCw className={`w-3.5 h-3.5 ${syncingId === sub.id ? 'animate-spin' : ''}`} />}
                aria-label="Synchronisieren"
                size="sm"
                variant="ghost"
                disabled={syncingId === sub.id}
                onClick={() => void handleSyncConfluence(sub)}
                title="Jetzt synchronisieren"
              />
              <IconButton
                icon={<Trash2 className="w-3.5 h-3.5" />}
                aria-label="Entfernen"
                size="sm"
                variant="ghost"
                onClick={() => setDeleteSubTarget(sub)}
                className="text-red-500 hover:text-red-700"
              />
            </div>
          )
        }
        const source = row.original._source!
        const isEditable = source.sourceType === 'text' || source.sourceType === 'email'
        return (
          <div className="flex items-center gap-1">
            <IconButton
              icon={<Eye className="w-3.5 h-3.5" />}
              aria-label="Inhalt anzeigen"
              size="sm"
              variant="ghost"
              onClick={() => setViewSource(source)}
              title="Inhalt anzeigen"
            />
            {isEditable && (
              <IconButton
                icon={<Pencil className="w-3.5 h-3.5" />}
                aria-label="Inhalt bearbeiten"
                size="sm"
                variant="ghost"
                onClick={() => setEditContentSource(source)}
                title="Inhalt bearbeiten"
              />
            )}
            <IconButton
              icon={<Tag className="w-3.5 h-3.5" />}
              aria-label="Metadaten bearbeiten"
              size="sm"
              variant="ghost"
              onClick={() => setEditSource(source)}
              title="Metadaten bearbeiten"
            />
            <IconButton
              icon={<Trash2 className="w-3.5 h-3.5" />}
              aria-label="Löschen"
              size="sm"
              variant="ghost"
              onClick={() => setDeleteSource(source)}
              className="text-red-500 hover:text-red-700"
            />
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <Card>
        <Card.Header className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Database className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">KI-Wissensspeicher</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Dokumente und Muster-Texte als Stil-Vorlagen für KI-gestützte Generierung.
                Quellen können global (alle Firmen) oder mandantenspezifisch sein.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleAnalyze} disabled={analyzing}>
              <Sparkles className="w-3.5 h-3.5" />
              {analyzing ? 'Analysiere…' : 'Optimieren'}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3.5 h-3.5" />
              Hinzufügen
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <p className="text-xs text-gray-500 font-medium mb-2">Verwendet von:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <Link to="/artikel?tab=content-standard" className="flex items-center gap-1 text-violet-600 hover:text-violet-800">
              <ExternalLink className="w-3 h-3" /> Artikel → Content-Standardisierung
            </Link>
            <Link to="/auftraege/angebot" className="flex items-center gap-1 text-violet-600 hover:text-violet-800">
              <ExternalLink className="w-3 h-3" /> Angebote → KI-Erstellung
            </Link>
            <span className="flex items-center gap-1 text-gray-400 cursor-default">
              <ExternalLink className="w-3 h-3" /> E-Mail → Versand (geplant)
            </span>
          </div>
        </Card.Body>
      </Card>

      {openaiConfigured === false && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          <span>
            <strong>OpenAI-API-Key nicht konfiguriert.</strong>{' '}
            Ohne diesen Key können keine Vektorembeddings berechnet werden — Quellen sind nicht via KI-Suche findbar.{' '}
            <Link to="/einstellungen?tab=adapters" className="underline font-medium hover:text-amber-900">
              Jetzt konfigurieren
            </Link>
            {' '}oder{' '}
            <Link to="/einstellungen?tab=jobs" className="underline font-medium hover:text-amber-900">
              Re-Embed-Job starten
            </Link>
            {' '}(nachdem der Key gesetzt ist).
          </span>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 w-16 shrink-0">Typ:</span>
          {(['all', ...ALL_USAGE_TAGS] as const).map((tag) => (
            <button key={tag} type="button" onClick={() => setFilterTag(tag)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filterTag === tag ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {tag === 'all' ? 'Alle' : KNOWLEDGE_USAGE_TAG_LABELS[tag]}
              {tag !== 'all' && (
                <span className="ml-1 opacity-70">
                  ({sources.filter((s) => (s.usageTags ?? []).includes(tag)).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {tenants.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500 w-16 shrink-0">Mandant:</span>
            {([
              { key: 'all', label: 'Alle Mandanten' },
              { key: 'global', label: 'Global' },
              ...tenants.map((t) => ({ key: t.id, label: t.name })),
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilterScope(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  filterScope === key
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {label}
                {key !== 'all' && (
                  <span className="ml-1 opacity-70">
                    ({key === 'global'
                      ? sources.filter((s) => s.scopeTenantId === null).length
                      : sources.filter((s) => s.scopeTenantId === key).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState title="Fehler" description={error} onRetry={load} />
      ) : unifiedData.length === 0 ? (
        <EmptyState
          title="Keine Wissensquellen"
          description={(() => {
            const isFiltered = filterTag !== 'all' || filterScope !== 'all'
            if (!isFiltered) return 'Füge Muster-Texte, Stil-Richtlinien oder Dokumente hinzu, die die KI als Vorlage verwenden soll.'
            const parts: string[] = []
            if (filterTag !== 'all') parts.push(`Typ "${KNOWLEDGE_USAGE_TAG_LABELS[filterTag]}"`)
            if (filterScope === 'global') parts.push('Scope "Global"')
            else if (filterScope !== 'all') {
              const name = tenants.find((t) => t.id === filterScope)?.name ?? filterScope
              parts.push(`Mandant "${name}"`)
            }
            return `Keine Quellen für ${parts.join(' + ')}.`
          })()}
          action={filterTag === 'all' && filterScope === 'all' ? (
            <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
              Erste Quelle hinzufügen
            </Button>
          ) : undefined}
        />
      ) : (
        <DataTable columns={columns} data={unifiedData} />
      )}

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Angebote scannen</h3>
        </div>
        <QuoteRagSection />
      </div>

      <AddSourceModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAdded={() => { void load() }}
        tenants={tenants}
      />
      <EditSourceModal source={editSource} onClose={() => setEditSource(null)} onSaved={load} tenants={tenants} />
      <EditContentModal source={editContentSource} onClose={() => setEditContentSource(null)} onSaved={() => { void load() }} />
      <SourceContentModal source={viewSource} onClose={() => setViewSource(null)} />
      <ConfirmDialog
        open={!!deleteSource}
        title="Wissensquelle löschen?"
        message={`"${deleteSource?.title}" und alle zugehörigen Chunks werden dauerhaft gelöscht.`}
        confirmLabel="Löschen"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteSource(null)}
      />
      <ConfirmDialog
        open={!!deleteSubTarget}
        title="Confluence-Abo entfernen?"
        message={`"${deleteSubTarget?.title}" und alle verknüpften Chunks werden dauerhaft gelöscht.`}
        confirmLabel="Entfernen"
        variant="danger"
        loading={deletingSub}
        onConfirm={handleDeleteSub}
        onCancel={() => setDeleteSubTarget(null)}
      />
      {analysisError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {analysisError}
        </div>
      )}
      {analysisResult && (
        <OptimizationResultModal
          open
          result={analysisResult}
          onClose={() => setAnalysisResult(null)}
          onPrioritiesApplied={load}
        />
      )}
    </div>
  )
}

// ─── Main tab component ───────────────────────────────────────────────────────

export function WissensspeicherTab({ initialSubTab }: { initialSubTab?: string }) {
  const [searchParams] = useSearchParams()
  const effective = initialSubTab ?? searchParams.get('wissensTab') ?? undefined
  const resolved = VALID_SUB_TABS.includes(effective as WissensSubTab)
    ? (effective as WissensSubTab)
    : 'wissenseintraege'
  const [activeSubTab, setActiveSubTab] = useState<WissensSubTab>(resolved)

  return (
    <Tabs value={activeSubTab} onValueChange={(v) => setActiveSubTab(v as WissensSubTab)}>
      <TabsList className="mb-6">
        <TabsTrigger value="wissenseintraege">
          <Brain className="w-3.5 h-3.5" />
          Wissenseinträge
        </TabsTrigger>
        <TabsTrigger value="wissensquellen">
          <Database className="w-3.5 h-3.5" />
          Wissensquellen
        </TabsTrigger>
      </TabsList>

      <TabsContent value="wissenseintraege">
        <WissenseintraegePanel />
      </TabsContent>

      <TabsContent value="wissensquellen">
        <WissensquellenPanel />
      </TabsContent>
    </Tabs>
  )
}
